import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import EditContainer from "../../components/global/editContainer"
import PageSpinner from "../../components/global/pageSpinner"
import { MainContext } from "../../controllers/main"
import { DialogTransition } from "../../services/utilities/utility"
import LoadingBackdrop from "../../components/global/loadingBackdrop"
import EditHeadingWithStages from "../../components/global/editHeadingWithStages"
import { Stage } from "../../services/config/enum"
import { SurveysContext } from "../../controllers/surveys"
import { SurveyLinkMode, SurveyProvider } from "../../models/survey"
import SurveyGeneralEdit from "../../components/survey/surveyGeneralEdit"
import { LanguagesContext } from "../../controllers/languages"
import { AddCircleRounded } from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import SurveyLinkChip from "../../components/survey/surveyLinkChip"
import SurveyLinkIcon from "../../components/survey/surveyLinkIcon"
import SurveyTranslationsEdit from "../../components/survey/surveyTranslationsEdit"
import CantPerformActionDialog from "../../components/global/cantPerformActionDialog"

const SurveyEdit = () => {
  const { animation, setAnimation, setChangesSaved, showPadding } =
    useContext(MainContext)
  const { languages, languagesForEpisodeTabs } = useContext(LanguagesContext)
  const {
    loading: controllerLoading,
    setLoading: setControllerLoading,
    editMode,
    setEditMode,
    doneChanges,
    cancelChanges,
    currentSurvey,
    getCurrentSurvey,
    preChangesCurrentSurvey,
    addTranslation,
    hasError,
    upsertSurvey,
    upsertSurveyDocument,
    publishSurvey,
    linkSurvey,
  } = useContext(SurveysContext)
  const { surveyId } = useParams()
  const navigate = useNavigate()

  // prevent tab close
  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault()
      console.log("beforeunload event triggered")
      return (event.returnValue = "Are you sure you want to exit?")
    }

    window.addEventListener("beforeunload", handleTabClose)

    return () => {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [])

  // loading
  const [loading, setLoading] = useState<boolean>(true)

  // page error
  const [pageError, setPageError] = useState<boolean>(false)

  // fetch current survey
  const fetchAll = async () => {
    const noErrors = await getCurrentSurvey(surveyId)
    if (noErrors) {
      setLoading(false)
      setControllerLoading(false)
    } else {
      setPageError(true)
    }
  }

  useEffect(() => {
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // tabs
  const [selectedTab, setSelectedTab] = useState<number>(0)

  // add translation dialog
  const [addTranslationOpen, setAddTranslationOpen] = useState<boolean>(false)
  const [translationToAdd, setTranslationToAdd] = useState<
    { value: string; label: string; flag?: string } | undefined
  >()

  const handleAddTranslationClose = () => {
    setAddTranslationOpen(false)
    setTimeout(() => {
      setTranslationToAdd(undefined)
    }, 150)
  }

  // no default translation alert dialog
  const [
    noDefaultTranslationAlertDialogOpen,
    setNoDefaultTranslationAlertDialogOpen,
  ] = useState<boolean>(false)

  // suspend survey
  const [suspendDialogOpen, setSuspendDialogOpen] = useState<boolean>(false)
  const [linkInfoDialogOpen, setLinkInfoDialogOpen] = useState<boolean>(false)

  return loading ? (
    <PageSpinner
      showBackButton={pageError}
      backButtonOnClick={() => {
        setAnimation(false)
        navigate("/surveys")
      }}
    />
  ) : (
    <EditContainer>
      <EditHeadingWithStages
        saveButtonLoading={controllerLoading}
        backButtonOnClick={() => {
          setAnimation(false)
          setTimeout(() => {
            navigate("/surveys")
          }, 300)
        }}
        title={currentSurvey.handle}
        id={preChangesCurrentSurvey.id}
        editModeActive={editMode}
        setEditModeActive={setEditMode}
        saveButtonDisabled={
          hasError ||
          (currentSurvey.stage === Stage.PUBLISHED && !doneChanges) ||
          (!doneChanges &&
            currentSurvey.stage === Stage.DRAFT &&
            currentSurvey.document.items.some(
              (item) => item.linkMode === SurveyLinkMode.unlinked
            ))
        }
        undoButtonDisabled={!doneChanges}
        doneChanges={doneChanges}
        undoButtonOnClick={() => {
          if (selectedTab > preChangesCurrentSurvey.document.items.length) {
            setSelectedTab(preChangesCurrentSurvey.document.items.length)
          }
          cancelChanges()
        }}
        saveButtonOnClick={async () => {
          if (!doneChanges && currentSurvey.stage === Stage.DRAFT) {
            setControllerLoading(true)
            const noErrors = await publishSurvey()

            if (noErrors) {
              setControllerLoading(false)
              setChangesSaved(true)
            } else {
              setControllerLoading(false)
            }

            return
          }

          if (
            currentSurvey.document.items.filter((item) => item.isDefault).length
          ) {
            setControllerLoading(true)
            const noErrors = await upsertSurvey(currentSurvey.suspended)
            if (noErrors) {
              const documentNoErrors = await upsertSurveyDocument()
              if (documentNoErrors) {
                setControllerLoading(false)
                setChangesSaved(true)
              } else {
                setControllerLoading(false)
              }
            } else {
              setControllerLoading(false)
            }
          } else {
            setNoDefaultTranslationAlertDialogOpen(true)
          }
        }}
        stage={doneChanges ? Stage.EDITED : currentSurvey.stage}
        showLinkStatus={currentSurvey.document.items.some(
          (item) => item.linkMode === SurveyLinkMode.unlinked
        )}
        linkInfoOnClick={() => setLinkInfoDialogOpen(true)}
        showSuspendButton
        isSuspended={currentSurvey.suspended}
        suspendButtonOnClick={() => {
          setSuspendDialogOpen(true)
        }}
      />
      <Grow
        in={animation}
        timeout={300}
        style={{
          margin: showPadding === "yes" ? 16 : 0,
          marginTop: 0,
        }}
      >
        <Stack>
          {/* languages tabs */}
          <Stack direction="row" justifyContent="flex-start">
            <Card
              variant="outlined"
              style={{
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: showPadding === "yes" ? null : 0,
                borderBottom: "none",
                borderLeft: showPadding === "yes" ? null : "none",
              }}
            >
              <Stack direction="row" justifyContent="flex-start">
                <Tabs
                  value={selectedTab}
                  onChange={(e: any, newValue: number) => {
                    setSelectedTab(newValue)
                  }}
                  aria-label="languages tabs"
                >
                  <Tab label="General" style={{ minWidth: 180 }} />
                  {currentSurvey.document.items.map((item) => (
                    <Tab
                      key={item.lang}
                      style={{ minWidth: 180 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>
                            {
                              languagesForEpisodeTabs.find(
                                (arrayItem) => arrayItem.value === item.lang
                              ).flag
                            }
                          </span>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <span>
                              {
                                languagesForEpisodeTabs.find(
                                  (arrayItem) => arrayItem.value === item.lang
                                ).label
                              }
                              {item.isDefault && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    marginLeft: 4,
                                    marginTop: 2,
                                  }}
                                >
                                  (Default)
                                </span>
                              )}
                            </span>
                            <SurveyLinkIcon linkMode={item.linkMode} small />
                          </Stack>
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
                <Divider orientation="vertical" variant="middle" flexItem />
                <div
                  style={{
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    disabled={!editMode}
                    aria-label="add language button"
                    component="span"
                    style={{ width: 48, height: 48 }}
                    onClick={() => {
                      setAddTranslationOpen(true)
                    }}
                  >
                    <AddCircleRounded />
                  </IconButton>
                </div>
              </Stack>
            </Card>
          </Stack>
          <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {!selectedTab ? (
              <SurveyGeneralEdit />
            ) : (
              <SurveyTranslationsEdit itemIndex={selectedTab - 1} />
            )}
          </Card>
        </Stack>
      </Grow>
      <LoadingBackdrop open={controllerLoading} />
      {/* add translation dialog */}
      <Dialog onClose={handleAddTranslationClose} open={addTranslationOpen}>
        <DialogTitle>Add translation</DialogTitle>
        {translationToAdd ? (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              Are you sure you want to add{" "}
              <b>{translationToAdd.label.toUpperCase()}</b>?
            </DialogContentText>
          </DialogContent>
        ) : languagesForEpisodeTabs.filter((item) => {
            if (
              item.value !== "general" &&
              !currentSurvey.document.items.some(
                (translationItem) => translationItem.lang === item.value
              )
            ) {
              return item
            }
          }).length ? (
          <List sx={{ pt: 0, minWidth: 300 }}>
            {languagesForEpisodeTabs
              .filter((item) => {
                if (
                  item.value !== "general" &&
                  !currentSurvey.document.items.some(
                    (translationItem) => translationItem.lang === item.value
                  )
                ) {
                  return item
                }
              })
              .map((item) => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setTranslationToAdd(item)
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <span>{item.flag}</span>
                      <span>{item.label.toUpperCase()}</span>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        ) : (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              You have already added all languages available
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleAddTranslationClose}>Cancel</Button>
          {translationToAdd && (
            <Button
              onClick={() => {
                addTranslation(translationToAdd.value)
                handleAddTranslationClose()
                setSelectedTab(currentSurvey.document.items.length)
              }}
            >
              Add {translationToAdd.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* no default translation alert dialog */}
      <CantPerformActionDialog
        open={noDefaultTranslationAlertDialogOpen}
        setOpen={setNoDefaultTranslationAlertDialogOpen}
        content="There must be a default translation"
      />
      {/* link info dialog */}
      <Dialog
        open={linkInfoDialogOpen}
        onClose={() => setLinkInfoDialogOpen(false)}
      >
        <DialogTitle>Unlinked Languages</DialogTitle>
        <DialogContent style={{ minWidth: 375 }}>
          <Stack gap={2}>
            {currentSurvey.document.items
              .filter((item) => item.linkMode === SurveyLinkMode.unlinked)
              .map((item) => (
                <Stack
                  key={item.lang}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Typography>
                    {
                      languagesForEpisodeTabs.find(
                        (lang) => lang.value === item.lang
                      ).label
                    }
                  </Typography>
                  <SurveyLinkChip linkMode={item.linkMode} small />
                </Stack>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkInfoDialogOpen(false)}>Close</Button>
          {currentSurvey.provider === SurveyProvider.typeform && (
            <LoadingButton
              loading={controllerLoading}
              onClick={async () => {
                setControllerLoading(true)
                const unlinkedLangs = currentSurvey.document.items
                  .filter((item) => item.linkMode === SurveyLinkMode.unlinked)
                  .map((item) => item.lang)
                const success = await linkSurvey(
                  SurveyLinkMode.auto,
                  unlinkedLangs
                )
                if (success) {
                  setLinkInfoDialogOpen(false)
                }
                setControllerLoading(false)
              }}
            >
              Link All Automatically
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      {/* suspend survey dialog */}
      <Dialog open={suspendDialogOpen} TransitionComponent={DialogTransition}>
        <DialogTitle>
          Are you sure you want to{" "}
          {currentSurvey.suspended ? "resume" : "suspend"} this Survey?
        </DialogTitle>
        <DialogContent>
          <Typography>
            {currentSurvey.stage !== Stage.PUBLISHED
              ? "All the changes will be published and "
              : ""}
            {currentSurvey.stage !== Stage.PUBLISHED ? "this Survey" : "It"}{" "}
            {currentSurvey.suspended
              ? "will be assigned to users again"
              : "will no longer be assigned to users"}
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={controllerLoading}
            onClick={() => {
              setSuspendDialogOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={controllerLoading}
            onClick={async () => {
              setSuspendDialogOpen(false)
              setControllerLoading(true)

              let noErrors: boolean

              noErrors = await upsertSurvey(!currentSurvey.suspended)
              if (noErrors) {
                noErrors = await publishSurvey()
                if (noErrors) {
                  setChangesSaved(true)
                }
              }

              setControllerLoading(false)
            }}
          >
            {currentSurvey.suspended ? "Resume" : "Suspend"}
          </Button>
        </DialogActions>
      </Dialog>
    </EditContainer>
  )
}

export default SurveyEdit
